import type {
  Action,
  CardDefId,
  GameEvent,
  GameState,
  PlayerId,
  TargetCandidate,
  TargetStepSelection,
  Zone,
} from '@cards/engine';
import { isDeckPassiveId, type DeckPassiveId } from '@cards/cards';
import { targetCandidateKey } from '@cards/engine';
import { MAX_NICKNAME_LENGTH, normalizeNickname } from './presence.js';

/**
 * Current wire schema; v16 replaces the former overload event/field with the
 * independent HeatChanged and FreezeChanged counters. v15 adds the committed `cellId` to public FIELD
 * `CardRevealed`, so every client can render successful and fizzled same-cell
 * intents symmetrically. v14 adds `causedByEventIndex` to reactive public
 * StatChanged payloads and an explicit `VarChanged.public` marker, letting
 * playback group a visible reaction with its cause while still redacting
 * undeclared vars. v12 (D55) adds the appearance/heal lifecycle events
 * UnitPlaced, UnitAppeared and UnitHealed, the typed `cause` field on
 * UnitEnteredPlay, and the enriched UnitDied.cause taxonomy
 * (combat|damage|destroyed|timed). Protocol-11 clients are not served a v12
 * stream: such rooms are classified as incompatible/SERVER_UPDATED.
 */
export const PROTOCOL_VERSION = 16;

export type RoomPhase = 'waiting_for_players' | 'choosing_decks' | 'playing';

export interface RoomPlayerState {
  playerId: PlayerId;
  /** Public display name supplied by the connected browser. */
  nickname?: string;
  connected: boolean;
  selected: boolean;
  locked: boolean;
  /** Temporary legacy preset id, present only for the viewer's own seat. */
  deckDefinitionId?: string;
  /** Temporary legacy preset name, present only for the viewer's own seat. */
  deckName?: string;
}

export interface RoomStateMsg {
  t: 'room_state';
  protocolVersion: typeof PROTOCOL_VERSION;
  seq: number;
  phase: RoomPhase;
  players: RoomPlayerState[];
}

export type RejectionCode =
  | 'INVALID_MESSAGE'
  | 'INVALID_ROOM_PHASE'
  | 'ACCOUNT_REQUIRED'
  | 'DECK_ARCHIVED'
  | 'DECK_INVALID'
  | 'DECK_REVISION_CONFLICT'
  | 'DECK_UNAVAILABLE'
  | 'DECK_LOCKED'
  | 'PROTOCOL_MISMATCH'
  | 'INVALID_SESSION'
  | 'SERVER_UPDATED';

/** JSON-only client protocol. Identity comes from the bound server session. */
export interface SavedDeckSelectionRef {
  deckId: string;
  revision: number;
}

export type SelectDeckClientMsg =
  | {
      t: 'select_deck';
      clientSeq: number;
      /** Temporary anonymous preset bridge. */
      deckDefinitionId: string;
      deck?: never;
    }
  | {
      t: 'select_deck';
      clientSeq: number;
      /** Account-owned reference; the browser never sends composition or owner data. */
      deck: SavedDeckSelectionRef;
      deckDefinitionId?: never;
    };

export type ClientMsg =
  | { t: 'set_nickname'; nickname: string }
  | { t: 'action'; action: Action; clientSeq: number }
  | SelectDeckClientMsg
  | { t: 'lock_deck'; clientSeq: number }
  | { t: 'resync' }
  | { t: 'ping'; ts: number };

export interface SerializedChoiceCandidate {
  choiceToken: string;
  candidate: TargetCandidate;
  label?: string;
}

/** Public, redacted runtime-choice descriptor. Token-to-authoritative-target data stays server-side. */
export interface SerializedChoice {
  choiceId: string;
  who: PlayerId;
  prompt?: string;
  kind?: string;
  cardinality: { min: number; max: number };
  ordered: boolean;
  candidates: SerializedChoiceCandidate[];
}

export interface ReplayDeckPlayerProjection {
  playerId: PlayerId;
  /** Exact 24-card starting deck; no saved-deck, template, owner or name metadata. */
  cardIds: CardDefId[];
  digest: string;
  /** Public authored passive identity fixed by the immutable match snapshot. */
  passiveId: DeckPassiveId | null;
}

/** Private participant projection delivered only after the authoritative ended snapshot. */
export interface ReplayDecksMsg {
  t: 'replay_decks';
  roomId: string;
  seq: number;
  players: ReplayDeckPlayerProjection[];
}

/**
 * `events` is informational in iteration 1. Every accepted action also sends a
 * full per-player `snapshot` with the same room revision (`seq`).
 */
export type ServerMsg =
  | {
      t: 'session';
      roomId: string;
      playerId: PlayerId;
      reconnectToken: string;
      protocolVersion: typeof PROTOCOL_VERSION;
      /** Authoritative first sequence accepted from this session after join/reconnect. */
      nextClientSeq: number;
    }
  | {
      t: 'snapshot';
      seq: number;
      state: GameState;
    }
  | {
      t: 'events';
      seq: number;
      events: GameEvent[];
      ackClientSeq?: number;
    }
  | {
      /** Durable history sent after a reconnect snapshot; never replayed as animation. */
      t: 'log_history';
      entries: Array<{ seq: number; event: GameEvent }>;
    }
  | { t: 'rejected'; clientSeq: number; reason: string; code?: RejectionCode }
  | { t: 'choose'; choice: SerializedChoice }
  | { t: 'pong'; ts: number }
  | ReplayDecksMsg
  | RoomStateMsg;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

function parseClientSeq(value: unknown, messageType: string): number {
  if (!Number.isSafeInteger(value) || (value as number) < 0) {
    throw new Error(`${messageType}.clientSeq должен быть неотрицательным safe integer`);
  }
  return value as number;
}

function parsePositiveSafeInteger(value: unknown, where: string): number {
  if (!Number.isSafeInteger(value) || (value as number) <= 0) {
    throw new Error(`${where} должен быть положительным safe integer`);
  }
  return value as number;
}

function assertOnlyKeys(value: Record<string, unknown>, allowed: readonly string[], where: string): void {
  const extra = Object.keys(value).find((key) => !allowed.includes(key));
  if (extra !== undefined) throw new Error(`${where} содержит неизвестное поле "${extra}"`);
}

function assertExactKeys(value: Record<string, unknown>, expected: readonly string[], where: string): void {
  assertOnlyKeys(value, expected, where);
  const missing = expected.find((key) => !Object.hasOwn(value, key));
  if (missing !== undefined) throw new Error(`${where} не содержит обязательное поле "${missing}"`);
}

const LOWERCASE_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/u;
const DECK_DIGEST = /^sha256:[0-9a-f]{64}$/u;

export function parseSavedDeckSelectionRef(value: unknown): SavedDeckSelectionRef {
  if (!isRecord(value)) throw new Error('select_deck.deck должен быть объектом');
  assertExactKeys(value, ['deckId', 'revision'], 'select_deck.deck');
  if (typeof value.deckId !== 'string' || !LOWERCASE_UUID.test(value.deckId)) {
    throw new Error('select_deck.deck.deckId должен быть canonical lowercase UUID');
  }
  return {
    deckId: value.deckId,
    revision: parsePositiveSafeInteger(value.revision, 'select_deck.deck.revision'),
  };
}

/** Strict parser for the new private ended-only server projection. */
export function parseReplayDecksMsg(value: unknown): ReplayDecksMsg {
  if (!isRecord(value)) throw new Error('replay_decks должен быть объектом');
  assertExactKeys(value, ['t', 'roomId', 'seq', 'players'], 'replay_decks');
  if (value.t !== 'replay_decks') throw new Error('ожидался replay_decks');
  if (!isNonEmptyString(value.roomId) || value.roomId.length > 128) {
    throw new Error('replay_decks.roomId должен быть bounded непустой строкой');
  }
  if (!Number.isSafeInteger(value.seq) || (value.seq as number) < 0) {
    throw new Error('replay_decks.seq должен быть неотрицательным safe integer');
  }
  if (!Array.isArray(value.players) || value.players.length !== 2) {
    throw new Error('replay_decks.players должен содержать ровно двух игроков');
  }
  const players = value.players.map((candidate, index): ReplayDeckPlayerProjection => {
    const where = `replay_decks.players[${index}]`;
    if (!isRecord(candidate)) throw new Error(`${where} должен быть объектом`);
    assertExactKeys(candidate, ['playerId', 'cardIds', 'digest', 'passiveId'], where);
    if (!isNonEmptyString(candidate.playerId) || candidate.playerId.length > 64) {
      throw new Error(`${where}.playerId должен быть bounded непустой строкой`);
    }
    if (!Array.isArray(candidate.cardIds) || candidate.cardIds.length !== 24
      || !candidate.cardIds.every((cardId) => typeof cardId === 'string'
        && cardId.length > 0 && cardId.length <= 256)) {
      throw new Error(`${where}.cardIds должен содержать ровно 24 bounded CardDefId`);
    }
    if (typeof candidate.digest !== 'string' || !DECK_DIGEST.test(candidate.digest)) {
      throw new Error(`${where}.digest должен иметь форму sha256:<64 lowercase hex>`);
    }
    if (candidate.passiveId !== null && !isDeckPassiveId(candidate.passiveId)) {
      throw new Error(`${where}.passiveId должен быть null или stable lowercase id`);
    }
    return {
      playerId: candidate.playerId,
      cardIds: [...candidate.cardIds] as CardDefId[],
      digest: candidate.digest,
      passiveId: candidate.passiveId as DeckPassiveId | null,
    };
  });
  if (new Set(players.map(({ playerId }) => playerId)).size !== players.length) {
    throw new Error('replay_decks.players не должен содержать повторные playerId');
  }
  return { t: 'replay_decks', roomId: value.roomId, seq: value.seq as number, players };
}

const TARGET_ZONES = ['deck', 'hand', 'board', 'discard', 'armed', 'attachment'] as const;

function parseTargetCandidate(value: unknown, where: string): TargetCandidate {
  if (!isRecord(value)) throw new Error(`${where} должен быть объектом`);

  switch (value.kind) {
    case 'unit':
      assertOnlyKeys(value, ['kind', 'instanceId'], where);
      if (!isNonEmptyString(value.instanceId)) throw new Error(`${where}.instanceId должен быть непустой строкой`);
      return { kind: 'unit', instanceId: value.instanceId };
    case 'card':
      assertOnlyKeys(value, ['kind', 'instanceId', 'zone'], where);
      if (!isNonEmptyString(value.instanceId)) throw new Error(`${where}.instanceId должен быть непустой строкой`);
      if (typeof value.zone !== 'string' || !(TARGET_ZONES as readonly string[]).includes(value.zone)) {
        throw new Error(`${where}.zone содержит неизвестную зону`);
      }
      return { kind: 'card', instanceId: value.instanceId, zone: value.zone as Zone };
    case 'cell':
      assertOnlyKeys(value, ['kind', 'cellId'], where);
      if (!isNonEmptyString(value.cellId)) throw new Error(`${where}.cellId должен быть непустой строкой`);
      return { kind: 'cell', cellId: value.cellId };
    case 'table':
      assertOnlyKeys(value, ['kind', 'tableId'], where);
      if (value.tableId !== 0 && value.tableId !== 1 && value.tableId !== 2) {
        throw new Error(`${where}.tableId должен быть 0, 1 или 2`);
      }
      return { kind: 'table', tableId: value.tableId };
    case 'player':
      assertOnlyKeys(value, ['kind', 'playerId'], where);
      if (!isNonEmptyString(value.playerId)) throw new Error(`${where}.playerId должен быть непустой строкой`);
      return { kind: 'player', playerId: value.playerId };
    case 'mode':
      assertOnlyKeys(value, ['kind', 'id'], where);
      if (!isNonEmptyString(value.id)) throw new Error(`${where}.id должен быть непустой строкой`);
      return { kind: 'mode', id: value.id };
    default:
      throw new Error(`${where}.kind содержит неизвестный вид цели "${String(value.kind)}"`);
  }
}

function parseTargetStepSelection(value: unknown, index: number): TargetStepSelection {
  const where = `PlanCard.selections[${index}]`;
  if (!isRecord(value)) throw new Error(`${where} должен быть объектом`);
  assertOnlyKeys(value, ['stepId', 'selection'], where);
  if (!isNonEmptyString(value.stepId)) throw new Error(`${where}.stepId должен быть непустой строкой`);
  if (!Array.isArray(value.selection)) throw new Error(`${where}.selection должен быть массивом`);

  const selection = value.selection.map((candidate, candidateIndex) => (
    parseTargetCandidate(candidate, `${where}.selection[${candidateIndex}]`)
  ));
  const keys = selection.map(targetCandidateKey);
  if (new Set(keys).size !== keys.length) {
    throw new Error(`${where}.selection не должен содержать повторные цели`);
  }
  return { stepId: value.stepId, selection };
}

function parseAction(value: unknown): Action {
  if (!isRecord(value) || !isNonEmptyString(value.playerId)) {
    throw new Error('action должен быть объектом с непустым playerId');
  }
  switch (value.kind) {
    case 'PlanCard': {
      assertOnlyKeys(value, [
        'kind', 'playerId', 'instanceId', 'selections',
        'cellId', 'mode', 'targetInstanceId', 'targetCellId',
      ], 'PlanCard');
      if (!isNonEmptyString(value.instanceId)) throw new Error('PlanCard требует instanceId');

      const legacyKeys = ['cellId', 'mode', 'targetInstanceId', 'targetCellId'] as const;
      const hasLegacySelection = legacyKeys.some((key) => Object.hasOwn(value, key));
      if (Object.hasOwn(value, 'selections')) {
        if (hasLegacySelection) {
          throw new Error('PlanCard не должен смешивать selections с legacy target-полями');
        }
        if (!Array.isArray(value.selections)) throw new Error('PlanCard.selections должен быть массивом');
        const selections = value.selections.map(parseTargetStepSelection);
        const stepIds = selections.map(({ stepId }) => stepId);
        if (new Set(stepIds).size !== stepIds.length) {
          throw new Error('PlanCard.selections не должен содержать повторные stepId');
        }
        return {
          kind: 'PlanCard', playerId: value.playerId, instanceId: value.instanceId, selections,
        };
      }

      for (const key of ['cellId', 'mode', 'targetInstanceId', 'targetCellId'] as const) {
        if (value[key] !== undefined && !isNonEmptyString(value[key])) {
          throw new Error(`PlanCard.${key} должен быть непустой строкой`);
        }
      }
      return {
        kind: 'PlanCard', playerId: value.playerId, instanceId: value.instanceId,
        ...(value.cellId !== undefined ? { cellId: value.cellId as string } : {}),
        ...(value.mode !== undefined ? { mode: value.mode as string } : {}),
        ...(value.targetInstanceId !== undefined ? { targetInstanceId: value.targetInstanceId as string } : {}),
        ...(value.targetCellId !== undefined ? { targetCellId: value.targetCellId as string } : {}),
      };
    }
    case 'UnplanCard':
      assertOnlyKeys(value, ['kind', 'playerId', 'instanceId'], 'UnplanCard');
      if (!isNonEmptyString(value.instanceId)) throw new Error('UnplanCard требует instanceId');
      return { kind: 'UnplanCard', playerId: value.playerId, instanceId: value.instanceId };
    case 'Ready':
      assertOnlyKeys(value, ['kind', 'playerId'], 'Ready');
      return { kind: 'Ready', playerId: value.playerId };
    case 'Resolve': {
      assertOnlyKeys(value, ['kind', 'playerId', 'choiceId', 'selection'], 'Resolve');
      if (!isNonEmptyString(value.choiceId) || !isRecord(value.selection)) {
        throw new Error('Resolve требует choiceId и selection');
      }
      assertOnlyKeys(value.selection, ['tokens'], 'Resolve.selection');
      const tokens = value.selection.tokens;
      if (!Array.isArray(tokens) || !tokens.every(isNonEmptyString)) {
        throw new Error('Resolve selection.tokens должен быть массивом непустых opaque tokens');
      }
      if (new Set(tokens).size !== tokens.length) {
        throw new Error('Resolve selection.tokens не должен содержать повторы');
      }
      return {
        kind: 'Resolve',
        playerId: value.playerId,
        choiceId: value.choiceId,
        selection: { tokens },
      };
    }
    default:
      throw new Error(`неизвестный kind Action "${String(value.kind)}"`);
  }
}

/** Parse one untrusted text frame into the deliberately small protocol union. */
export function parseClientMsg(text: string): ClientMsg {
  let raw: unknown;
  try {
    raw = JSON.parse(text) as unknown;
  } catch {
    throw new Error('сообщение должно быть валидным JSON');
  }
  if (!isRecord(raw)) throw new Error('сообщение должно быть JSON-объектом');
  switch (raw.t) {
    case 'set_nickname': {
      assertExactKeys(raw, ['t', 'nickname'], 'set_nickname');
      if (typeof raw.nickname !== 'string') throw new Error('set_nickname.nickname должен быть строкой');
      const nickname = normalizeNickname(raw.nickname);
      if (nickname.length === 0 || Array.from(nickname).length > MAX_NICKNAME_LENGTH
        || /\p{C}/u.test(nickname)) {
        throw new Error('set_nickname.nickname имеет неверный формат');
      }
      return { t: 'set_nickname', nickname };
    }
    case 'resync':
      assertExactKeys(raw, ['t'], 'resync');
      return { t: 'resync' };
    case 'ping':
      assertExactKeys(raw, ['t', 'ts'], 'ping');
      if (typeof raw.ts !== 'number' || !Number.isFinite(raw.ts)) {
        throw new Error('ping.ts должен быть конечным числом');
      }
      return { t: 'ping', ts: raw.ts };
    case 'action':
      assertExactKeys(raw, ['t', 'action', 'clientSeq'], 'action');
      return {
        t: 'action',
        action: parseAction(raw.action),
        clientSeq: parseClientSeq(raw.clientSeq, 'action'),
      };
    case 'select_deck':
      if (Object.hasOwn(raw, 'deck') && Object.hasOwn(raw, 'deckDefinitionId')) {
        throw new Error('select_deck не должен смешивать custom и legacy форму');
      }
      if (Object.hasOwn(raw, 'deck')) {
        assertExactKeys(raw, ['t', 'clientSeq', 'deck'], 'select_deck');
        return {
          t: 'select_deck',
          clientSeq: parseClientSeq(raw.clientSeq, 'select_deck'),
          deck: parseSavedDeckSelectionRef(raw.deck),
        };
      }
      assertExactKeys(raw, ['t', 'clientSeq', 'deckDefinitionId'], 'select_deck');
      if (!isNonEmptyString(raw.deckDefinitionId) || raw.deckDefinitionId.length > 128) {
        throw new Error('select_deck.deckDefinitionId должен быть bounded непустой строкой');
      }
      return {
        t: 'select_deck',
        deckDefinitionId: raw.deckDefinitionId,
        clientSeq: parseClientSeq(raw.clientSeq, 'select_deck'),
      };
    case 'lock_deck':
      assertExactKeys(raw, ['t', 'clientSeq'], 'lock_deck');
      return { t: 'lock_deck', clientSeq: parseClientSeq(raw.clientSeq, 'lock_deck') };
    default:
      throw new Error(`неизвестный тип сообщения "${String(raw.t)}"`);
  }
}
